import {
  getNativeBridge,
  getNativePlatform,
  isNativeBridgeAvailable,
} from "./nativeBridge";
import { requestPushRegistrationIntent } from "../../api/notifications/pushRegistrationIntent";

import { nativeInstallationSession } from "./installationSessionSignal";

// No session cookie, user identifier or native installation credential crosses
// this boundary. An intent belongs to one observed session generation only.
async function associate(bridge, platform, isCurrent = () => true) {
  try {
    if (!isCurrent()) return false;
    const response = await requestPushRegistrationIntent(platform);
    if (!isCurrent()) return false;
    if (!response?.ok) {
      console.warn("Native installation intent request was not successful.");
      return false;
    }
    const data = await response.json();
    if (!isCurrent()) return false;
    const intent = data?.intent_token;
    if (typeof intent !== "string" || !intent || intent.length > 64) {
      console.warn("Native installation intent response was invalid.");
      return false;
    }
    const result = await bridge.associateAuthenticatedInstallation(intent);
    if (!isCurrent()) return false;
    if (result?.status !== "associated") {
      console.warn("Native installation association did not succeed.");
      return false;
    }
    return true;
  } catch {
    // Never log exceptions/response bodies: a transport or bridge error can
    // embed an intent or other sensitive values in its message.
    console.error("Native installation association could not complete.");
    return false;
  }
}

/** Existing explicit-login entry point; Android behavior is preserved. iOS
 * uses the store middleware so login and session hydration cannot duplicate it. */
export async function associateAuthenticatedInstallationIfNative() {
  if (!isNativeBridgeAvailable()) return;
  const platform = getNativePlatform();
  if (!platform || (platform === "ios" && nativeInstallationSession.current))
    return;
  await associate(getNativeBridge(), platform);
}

function accountKey(auth) {
  return auth?.user?.email || auth?.user?.user_name || null;
}

/** Observe the existing successful auth actions, including cookie hydration.
 * No polling, login blocking, Redux auth mutation, or browser/Android requests.
 * State is per store/document and is never persisted. */
export function createIOSInstallationSessionMiddleware() {
  let epoch = 0;
  let account = null;
  let busy = false;
  let completed = false;
  let logoutStarted = false;
  let profileRequest = null;
  const owner = {
    invalidate() {
      epoch += 1;
      account = null;
      busy = false;
      completed = false;
      logoutStarted = true;
    },
  };

  function signalLogout() {
    if (getNativePlatform() !== "ios") return;
    try {
      const bridge = getNativeBridge();
      if (typeof bridge?.requestLogout === "function") {
        Promise.resolve(bridge.requestLogout()).catch(() => {});
      }
    } catch {
      /* A native failure never interrupts Redux/logout. */
    }
  }

  const middleware = ({ getState }) => {
    nativeInstallationSession.current = owner;
    return (next) => (action) => {
      if (action.type === "auth/fetchUserProfile/pending") {
        profileRequest = { id: action.meta?.requestId, epoch };
      }
      const previous = getState().auth;
      const result = next(action);
      const auth = getState().auth;
      if (action.type === "auth/logoutRdx") {
        const alreadySignaled = logoutStarted;
        owner.invalidate();
        if (!alreadySignaled) signalLogout();
        return result;
      }
      if (auth?.showSessionExpiredModal && !previous?.showSessionExpiredModal) {
        // Keep the existing modal/App Lock semantics, but discard pending
        // intents while the session cannot be trusted for new association.
        epoch += 1;
        busy = false;
        completed = false;
        return result;
      }
      const login = action.type === "auth/loginRdx";
      const hydrated = action.type === "auth/fetchUserProfile/fulfilled";
      if (!login && !hydrated) return result;
      if (
        hydrated &&
        (logoutStarted ||
          !profileRequest ||
          profileRequest.id !== action.meta?.requestId ||
          profileRequest.epoch !== epoch)
      )
        return result;
      const nextAccount = accountKey(auth);
      if (
        !auth?.isAuthenticated ||
        auth.isLoading ||
        auth.showSessionExpiredModal ||
        !nextAccount
      )
        return result;
      if (login || (account !== null && account !== nextAccount)) {
        epoch += 1;
        busy = false;
        completed = false;
        if (account !== null && account !== nextAccount) signalLogout();
      }
      logoutStarted = false;
      account = nextAccount;
      if (
        busy ||
        completed ||
        getNativePlatform() !== "ios" ||
        !isNativeBridgeAvailable()
      )
        return result;
      const bridge = getNativeBridge();
      const ticket = epoch;
      const isCurrent = () => {
        const current = getState().auth;
        return (
          epoch === ticket &&
          !logoutStarted &&
          current?.isAuthenticated &&
          !current.showSessionExpiredModal &&
          accountKey(current) === nextAccount &&
          getNativeBridge() === bridge &&
          getNativePlatform() === "ios"
        );
      };
      busy = true;
      void associate(bridge, "ios", isCurrent).then((success) => {
        if (epoch !== ticket) return;
        busy = false;
        completed = success;
      });
      return result;
    };
  };
  // Useful for a disposed store/HMR and for isolated local verification.
  middleware.dispose = () => {
    owner.invalidate();
    if (nativeInstallationSession.current === owner)
      nativeInstallationSession.current = null;
  };
  return middleware;
}
